import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAchievementToast } from "@/components/achievements/AchievementToast";

export interface Achievement {
  id: string;
  key: string;
  name: string;
  description: string;
  icon: string;
  category: string;
  points: number;
  badge_color: string;
  requirement_count: number;
}

export interface UserAchievement {
  id: string;
  achievement_id: string;
  progress: number;
  unlocked_at: string | null;
  notified: boolean;
}

export function useAchievements() {
  const queryClient = useQueryClient();
  const { showAchievementToast } = useAchievementToast();

  // Buscar todas as conquistas disponíveis
  const { data: achievements } = useQuery({
    queryKey: ["achievements"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("achievements")
        .select("*")
        .order("points", { ascending: true });

      if (error) throw error;
      return data as Achievement[];
    },
  });

  // Buscar conquistas do usuário
  const { data: userAchievements } = useQuery({
    queryKey: ["user-achievements"],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return [];

      const { data, error } = await supabase
        .from("user_achievements")
        .select("*")
        .eq("user_id", user.id);

      if (error) throw error;
      return data as UserAchievement[];
    },
  });

  // Atualizar progresso de uma conquista
  const updateProgress = useMutation({
    mutationFn: async ({
      achievementKey,
      increment = 1,
    }: {
      achievementKey: string;
      increment?: number;
    }) => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Not authenticated");

      // Buscar a conquista
      const achievement = achievements?.find((a) => a.key === achievementKey);
      if (!achievement) throw new Error("Achievement not found");

      // Buscar progresso atual
      const { data: existing } = await supabase
        .from("user_achievements")
        .select("*")
        .eq("user_id", user.id)
        .eq("achievement_id", achievement.id)
        .maybeSingle();

      const newProgress = (existing?.progress || 0) + increment;
      const shouldUnlock = newProgress >= achievement.requirement_count && !existing?.unlocked_at;

      if (existing) {
        // Atualizar existente
        const { data, error } = await supabase
          .from("user_achievements")
          .update({
            progress: newProgress,
            unlocked_at: shouldUnlock ? new Date().toISOString() : existing.unlocked_at,
          })
          .eq("id", existing.id)
          .select()
          .single();

        if (error) throw error;
        return { data, achievement, shouldUnlock };
      } else {
        // Criar novo
        const { data, error } = await supabase
          .from("user_achievements")
          .insert({
            user_id: user.id,
            achievement_id: achievement.id,
            progress: newProgress,
            unlocked_at: shouldUnlock ? new Date().toISOString() : null,
          })
          .select()
          .single();

        if (error) throw error;
        return { data, achievement, shouldUnlock };
      }
    },
    onSuccess: ({ achievement, shouldUnlock }) => {
      queryClient.invalidateQueries({ queryKey: ["user-achievements"] });
      queryClient.invalidateQueries({ queryKey: ["user_points"] });

      if (shouldUnlock) {
        // Mostrar toast de conquista desbloqueada
        showAchievementToast(achievement);
      }
    },
  });

  // Verificar e desbloquear conquistas automaticamente
  const checkAchievements = async (type: string) => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return;

    // Mapeamento de tipos para keys de conquistas (apenas eventos sem trigger no banco)
    const achievementMap: Record<string, string[]> = {
      shared: ["sharer"],
      referral_converted: ["ambassador"],
    };

    const achievementKeys = achievementMap[type];
    if (!achievementKeys) return;

    // Atualizar progresso de cada conquista relacionada
    for (const key of achievementKeys) {
      await updateProgress.mutateAsync({ achievementKey: key, increment: 1 });
    }
  };

  return {
    achievements,
    userAchievements,
    updateProgress: updateProgress.mutate,
    checkAchievements,
    isLoading: updateProgress.isPending,
  };
}
